'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import QuoteContactForm from './QuoteContactForm';
import ConsultationForm from './ConsultationForm';
import AuditForm from './AuditForm';

type ContactType = 'quote' | 'consultation' | 'audit' | null;

const contactOptions = [
{
  type: 'quote' as ContactType,
  title: 'Request a Quote',
  icon: 'DocumentTextIcon',
  description: 'Get detailed pricing for your manufacturing project with technical specifications and file uploads.',
  features: ['Technical drawing upload', 'Material selection', 'Quantity pricing', '24-48 hour response'],
  responseTime: '< 2 hours',
  color: 'primary'
},
{
  type: 'consultation' as ContactType,
  title: 'Schedule Consultation',
  icon: 'ChatBubbleLeftRightIcon',
  description: 'Discuss your technical requirements with our expert team for complex or specialized projects.',
  features: ['Expert technical guidance', 'Process recommendations', 'Timeline planning', 'Custom solutions'],
  responseTime: '< 4 hours',
  color: 'secondary'
},
{
  type: 'audit' as ContactType,
  title: 'Audit Request',
  icon: 'ShieldCheckIcon',
  description: 'Request compliance documentation, certifications, and audit materials for quality assurance.',
  features: ['ISO certifications', 'Audit documentation', 'Compliance verification', 'Quality records'],
  responseTime: '< 24 hours',
  color: 'accent'
}];


export default function ContactPathways() {
  const [selectedType, setSelectedType] = useState<ContactType>(null);

  const handleBack = () => {
    setSelectedType(null);
  };

  if (selectedType) {
    return (
      <section className="py-14 lg:py-22 bg-gradient-to-br from-primary via-brand-primary to-secondary">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-white hover:text-white/80 transition-all duration-base mb-8">

              <Icon name="ChevronLeftIcon" size={20} variant="outline" />
              <span className="font-cta font-semibold">Back to Contact Options</span>
            </button>
            
            {selectedType === 'quote' && <QuoteContactForm />}
            {selectedType === 'consultation' && <ConsultationForm />}
            {selectedType === 'audit' && <AuditForm />}
          </div>
        </div>
      </section>);

  }

  return (
    <section className="py-14 lg:py-22 bg-gradient-to-br from-primary via-brand-primary to-secondary">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl lg:text-5xl font-headline font-bold text-white mb-4">
            Contact Hub
          </h1>
          <p className="text-lg text-white/90 font-body">
            Choose the pathway that best fits your needs. Our intelligent routing system ensures you connect with the right team member.
          </p>
        </div>

        {/* Contact Options */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {contactOptions.map((option) =>
          <div
            key={option.type}
            className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-base cursor-pointer group"
            onClick={() => setSelectedType(option.type)}>

              <div className={`w-16 h-16 bg-${option.color}/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-base`}>
                <Icon name={option.icon as any} size={32} variant="solid" className={`text-${option.color}`} />
              </div>
              <h3 className="text-2xl font-headline font-bold text-foreground mb-3">
                {option.title}
              </h3>
              <p className="text-muted-foreground font-body mb-6">
                {option.description}
              </p>
              <ul className="space-y-2 mb-6">
                {option.features.map((feature, index) =>
              <li key={index} className="flex items-center space-x-2 text-sm text-foreground font-body">
                    <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success" />
                    <span>{feature}</span>
                  </li>
              )}
              </ul>
              <div className="flex items-center justify-between pt-6 border-t border-border">
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground font-body">Response Time</div>
                  <div className="text-sm font-cta font-bold text-foreground">{option.responseTime}</div>
                </div>
                <div className="flex items-center space-x-2 text-primary font-cta font-semibold">
                  <span>Select</span>
                  <Icon name="ArrowRightIcon" size={16} variant="outline" className="group-hover:translate-x-1 transition-transform duration-base" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Emergency Contact */}
        <div className="max-w-4xl mx-auto bg-error/10 backdrop-blur-sm rounded-2xl p-8 border border-error/20">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-error/20 rounded-xl flex items-center justify-center">
              <Icon name="ExclamationTriangleIcon" size={24} variant="solid" className="text-error" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-headline font-bold text-white mb-2">
                Emergency Production Support
              </h3>
              <p className="text-white/90 font-body mb-4">
                For urgent production issues or time-critical projects requiring immediate attention.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:+94274730309"
                  className="inline-flex items-center justify-center space-x-2 px-6 py-3 bg-white text-error rounded-lg font-cta font-bold hover:bg-white/90 transition-all duration-base">

                  <Icon name="PhoneIcon" size={20} variant="solid" />
                  <span>+84 27 4730 3090</span>
                </a>
                <a
                  href="mailto:info@fusionfinishing.net"
                  className="inline-flex items-center justify-center space-x-2 px-6 py-3 bg-white/10 backdrop-blur-sm text-white rounded-lg font-cta font-bold hover:bg-white/20 transition-all duration-base border border-white/20">

                  <Icon name="EnvelopeIcon" size={20} variant="outline" />
                  <span>Emergency Email</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Facility Information */}
        <div className="max-w-4xl mx-auto mt-8 bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-headline font-bold text-white mb-4">
                Visit Our Facility
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Icon name="MapPinIcon" size={20} variant="solid" className="text-white/80 flex-shrink-0 mt-1" />
                  <div className="text-white/90 font-body">
                    <div className="font-cta font-semibold text-white">Address</div>
                    Lot B2-56, N2 Street, Tan Dong Hiep B Industrial Park<br />
                    Ward​ Tan Dong Hiep, Ho Chi Minh City, Vietnam
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="ClockIcon" size={20} variant="solid" className="text-white/80 flex-shrink-0 mt-1" />
                  <div className="text-white/90 font-body">
                    <div className="font-cta font-semibold text-white">Hours</div>
                    Monday - Friday: 7:00 AM - 6:00 PM<br />
                    Saturday: 8:00 AM - 2:00 PM
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-headline font-bold text-white mb-4">
                General Contact
              </h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Icon name="PhoneIcon" size={20} variant="solid" className="text-white/80" />
                  <a href="tel:+842747303090" className="text-white/90 font-body hover:text-white transition-colors">+84 27 4730 3090

                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="EnvelopeIcon" size={20} variant="solid" className="text-white/80" />
                  <a href="mailto:info@fusionfinishing.net" className="text-white/90 font-body hover:text-white transition-colors">
                    info@fusionfinishing.net
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}